import { ReactNode } from "react";
import { useAuthContext } from "providers/Auth";
import { SectionCard } from "shared/app/SectionCard";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { Select, Show, TextArea } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { type Register } from "shared/hooks";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PropertyKind } from "shared/types";
import { PmsAddressValue } from "types/landlord/address";
import { states } from "utils/stateCodes";
import {
  ADDRESS_LABEL,
  formatPmsAddress,
  pmsManualTooltip,
  pmsSyncedTooltip
} from "./constants";

interface GeneralPmsValues {
  title?: string;
  description?: string;
  address?: PmsAddressValue;
}

interface GeneralPmsOverrides {
  title?: boolean;
  description?: boolean;
  address?: boolean;
}

const READ_ONLY_INPUT_CLASS = "[&_.bg-white]:!bg-alpha-40";
// TextArea passes readOnly/disabled straight to the native element and styles
// neither, so a synced description has to carry BaseInput's read-only look
// itself — and `disabled` rather than `readOnly`, so it cannot be focused or
// clicked into like the greyed-out inputs above it.
const DISABLED_TEXTAREA_CLASS = "bg-alpha-40 text-content-100";
const TOOLTIP_CLASS =
  "max-w-[320px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white";

// A missing key means "synced", same as an explicit `true` — the API only
// writes `false` once a partner picks "Edit manually".
const isSyncedWithPms = (
  overrides: GeneralPmsOverrides | undefined,
  key: keyof GeneralPmsOverrides
) => overrides?.[key] !== false;

interface PMSUnitGeneralFormProps {
  register: Register;
  values: Record<string, unknown>;
  unitPmsId?: string;
  propertyKind?: PropertyKind | null;
  headerExtra?: ReactNode;
  footer?: ReactNode;
}

export const PMSUnitGeneralForm = ({
  register,
  values,
  unitPmsId,
  propertyKind,
  headerExtra,
  footer
}: PMSUnitGeneralFormProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();

  const pmsValues = values.pms_values as GeneralPmsValues | undefined;
  const overrides = values.pms_overrides as GeneralPmsOverrides | undefined;

  const canOverride = Boolean(unitPmsId) && type.isBoomScreen;
  const titleSynced = isSyncedWithPms(overrides, "title");
  const descriptionSynced = isSyncedWithPms(overrides, "description");
  const addressSynced = isSyncedWithPms(overrides, "address");

  const titleReadOnly = Boolean(unitPmsId) && titleSynced;
  const descriptionReadOnly = Boolean(unitPmsId) && descriptionSynced;
  const addressReadOnly = Boolean(unitPmsId) && addressSynced;

  const nameLabel = `${dictionary.Unit} name`;

  const setOverride = (key: keyof GeneralPmsOverrides, value: boolean) => {
    register("pms_overrides").onChange({ ...(overrides || {}), [key]: value });
  };

  // Switching back to "Sync with PMS" restores the latest value the PMS sent,
  // so what the partner saves matches what they see.
  //
  // Units that have not synced since the reference values were introduced have
  // no snapshot yet; leave the field alone rather than blanking it.
  const handleTitleSync = (value: boolean) => {
    setOverride("title", value);
    if (value && pmsValues?.title) register("title").onChange(pmsValues.title);
  };

  const handleDescriptionSync = (value: boolean) => {
    setOverride("description", value);
    if (value && pmsValues?.description)
      register("description").onChange(pmsValues.description);
  };

  // The address group repopulates in both directions. The ticket asks for the
  // manual starting point to be "whatever the PMS currently has", and the
  // form's prefill can be staler than the snapshot — a unit whose PMS address
  // changed since the last sync, or one with no address record of its own.
  const handleAddressSync = (value: boolean) => {
    setOverride("address", value);

    const pmsAddress = pmsValues?.address;
    if (!pmsAddress?.address1) return;

    register("address1").onChange(pmsAddress.address1);
    register("address2").onChange(pmsAddress.address2 ?? "");
    register("city").onChange(pmsAddress.city ?? "");
    register("state_or_province").onChange(pmsAddress.state_or_province ?? "");
    register("postal_code").onChange(pmsAddress.postal_code ?? "");
  };

  return (
    <SectionCard title="General" headerExtra={headerExtra} footer={footer}>
      <div className="divide-y divide-alpha-40">
        <div className="grid grid-cols-2 gap-4 items-center pb-4">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <BaseInput
                  id="title"
                  label={nameLabel}
                  placeholder={titleReadOnly ? "-" : `${dictionary.Unit} 1`}
                  required
                  readOnly={titleReadOnly}
                  className={titleReadOnly ? READ_ONLY_INPUT_CLASS : ""}
                  value={register("title").value ?? ""}
                  onChange={(val) => register("title").onChange(val)}
                  error={register("title").error}
                />
              </TooltipTrigger>
              <TooltipContent className={TOOLTIP_CLASS}>
                <p>
                  {titleSynced
                    ? pmsSyncedTooltip(nameLabel)
                    : pmsManualTooltip(nameLabel)}
                </p>
              </TooltipContent>
            </Tooltip>
            <Show if={canOverride}>
              <PmsSyncOption
                name="title"
                value={titleSynced}
                onChange={handleTitleSync}
              />
            </Show>
          </div>
          <PMSValue value={pmsValues?.title} />
        </div>

        {/* Site ID is never editable, synced or not — no override for it. */}
        <div className="grid grid-cols-2 gap-4 py-4">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <BaseInput
                  id="externalId"
                  label={`${dictionary.Unit} ID`}
                  placeholder="-"
                  readOnly
                  className={READ_ONLY_INPUT_CLASS}
                  value={register("externalId").value ?? ""}
                  onChange={(val) => register("externalId").onChange(val)}
                  error={register("externalId").error}
                />
              </TooltipTrigger>
              <TooltipContent className={TOOLTIP_CLASS}>
                {dictionary.Unit} ID is never editable, synced or not
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 items-center">
            <div>
              <Tooltip>
                <TooltipTrigger className="w-full text-left">
                  <p className="text-base font-medium text-content-1000">
                    {ADDRESS_LABEL}
                  </p>
                </TooltipTrigger>
                <TooltipContent className={TOOLTIP_CLASS}>
                  <p>
                    {addressSynced
                      ? pmsSyncedTooltip(ADDRESS_LABEL)
                      : pmsManualTooltip(ADDRESS_LABEL)}
                  </p>
                </TooltipContent>
              </Tooltip>
              <Show if={canOverride}>
                <PmsSyncOption
                  name="address"
                  value={addressSynced}
                  onChange={handleAddressSync}
                />
              </Show>
            </div>
            <PMSValue value={formatPmsAddress(pmsValues?.address)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="w-full">
              <BaseInput
                id="address1"
                label="Street address"
                placeholder="2972 Westheimer Rd"
                required
                readOnly={addressReadOnly}
                className={addressReadOnly ? READ_ONLY_INPUT_CLASS : ""}
                value={register("address1").value ?? ""}
                onChange={(val) => register("address1").onChange(val)}
                error={register("address1").error}
              />
            </div>
            <div className="w-full">
              <BaseInput
                id="address2"
                label="Apartment, suite, etc."
                placeholder="Apartment 12"
                readOnly={addressReadOnly}
                className={addressReadOnly ? READ_ONLY_INPUT_CLASS : ""}
                value={register("address2").value ?? ""}
                onChange={(val) => register("address2").onChange(val)}
                error={register("address2").error}
              />
            </div>
            <div className="w-full">
              <BaseInput
                id="city"
                label="City"
                placeholder="Austin"
                required
                readOnly={addressReadOnly}
                className={addressReadOnly ? READ_ONLY_INPUT_CLASS : ""}
                value={register("city").value ?? ""}
                onChange={(val) => register("city").onChange(val)}
                error={register("city").error}
              />
            </div>
            <div className="flex w-full justify-end gap-4">
              <div className="mb-auto h-fit flex-1">
                <Select
                  id="state_or_province"
                  label="State"
                  placeholder="State"
                  searchable
                  hideOptional
                  options={states}
                  isDisabled={addressReadOnly}
                  showErrorMessage
                  {...register("state_or_province")}
                />
              </div>
              <div className="flex-1">
                <BaseInput
                  id="postal_code"
                  label="Zip"
                  placeholder="11111"
                  required
                  readOnly={addressReadOnly}
                  className={addressReadOnly ? READ_ONLY_INPUT_CLASS : ""}
                  value={register("postal_code").value ?? ""}
                  onChange={(val) => register("postal_code").onChange(val)}
                  error={register("postal_code").error}
                />
              </div>
            </div>
          </div>
        </div>

        <Show if={propertyKind || type.isPmsListingSyncEnabled}>
          <div className="grid grid-cols-2 gap-4 items-center py-4">
            <div>
              {/* The trigger renders a <button>, so it carries the label
                  instead of wrapping the textarea itself. */}
              <Tooltip>
                <TooltipTrigger className="w-full text-left">
                  <p className="mb-2 text-base font-medium text-content-1000">
                    Description
                  </p>
                </TooltipTrigger>
                <TooltipContent className={TOOLTIP_CLASS}>
                  <p>
                    {descriptionSynced
                      ? pmsSyncedTooltip("Description")
                      : pmsManualTooltip("Description")}
                  </p>
                </TooltipContent>
              </Tooltip>
              <TextArea
                id="description"
                placeholder="240"
                rows={4}
                autoExpand={true}
                disabled={descriptionReadOnly}
                className={descriptionReadOnly ? DISABLED_TEXTAREA_CLASS : ""}
                {...register("description")}
              />
              <Show if={canOverride}>
                <PmsSyncOption
                  name="description"
                  value={descriptionSynced}
                  onChange={handleDescriptionSync}
                />
              </Show>
            </div>
            <PMSValue value={pmsValues?.description} />
          </div>
        </Show>
      </div>
    </SectionCard>
  );
};
